export const routes = {
    // Accounts come from invitations; owners reset and confirm from emailed links.
    invitations: {
      base: "/invitations",
      preview: "/invitations/preview",
      accept: "/invitations/accept",
      acceptGoogle: "/invitations/accept-google",
      resend: (id: string) => `/invitations/${id}/resend`,
      revoke: (id: string) => `/invitations/${id}/revoke`,
    },
    // A vendor admin's own team.
    supportSessions: {
      base: "/support-sessions",
      token: (id: string) => `/support-sessions/${id}/token`,
      end: (id: string) => `/support-sessions/${id}/end`,
      forVendor: (vendorId: string) => `/support-sessions?vendor=${vendorId}`,
      mine: "/services/pms/support-sessions",
      endCurrent: "/services/pms/support-sessions/current/end",
    },
    team: {
      base: "/services/pms/team",
      invitations: "/services/pms/team/invitations",
      resend: (id: string) => `/services/pms/team/invitations/${id}/resend`,
      revoke: (id: string) => `/services/pms/team/invitations/${id}/revoke`,
    },
    account: {
      forgotPassword: "/account/forgot-password",
      checkPasswordReset: "/account/reset-password/check",
      resetPassword: "/account/reset-password",
      verifyEmail: "/account/verify-email",
      resendVerification: "/account/resend-verification",
      adminPasswordReset: (userId: string) => `/account/users/${userId}/password-reset`,
    },
    auth: {
      me: "/users/me",
      login: "/user/signin",
      refreshTokens: "/user/refresh",
      signout: "/user/signout",
    },
    adminOverview: "/services/pms/admin/overview",
    adminCosts: "/services/pms/admin/costs",
    adminDevEnv: "/services/pms/admin/dev-env",
    internalTraffic: {
      previewToken: "/services/pms/internal-traffic/preview-token",
      devices: "/services/pms/internal-traffic/devices",
      device: (id: string) => `/services/pms/internal-traffic/devices/${id}`,
    },
    support: {
      conversations: "/services/pms/support/conversations",
      conversation: (id: string) => `/services/pms/support/conversations/${id}`,
      messages: (id: string) => `/services/pms/support/conversations/${id}/messages`,
      read: (id: string) => `/services/pms/support/conversations/${id}/read`,
      unread: "/services/pms/support/unread",
      attachments: "/services/pms/support/attachments",
    },
    supportAdmin: {
      conversations: "/services/pms/admin/support/conversations",
      conversation: (id: string) => `/services/pms/admin/support/conversations/${id}`,
      messages: (id: string) => `/services/pms/admin/support/conversations/${id}/messages`,
      read: (id: string) => `/services/pms/admin/support/conversations/${id}/read`,
      summary: "/services/pms/admin/support/summary",
      staff: "/services/pms/admin/support/staff",
      attachments: "/services/pms/admin/support/attachments",
      tasks: "/services/pms/admin/support/tasks",
      task: (id: string) => `/services/pms/admin/support/tasks/${id}`,
      move: (id: string) => `/services/pms/admin/support/tasks/${id}/move`,
      link: (id: string) => `/services/pms/admin/support/tasks/${id}/link`,
    },
    devices: {
      overview: "/services/pms/devices",
      device: (id: string) => `/services/pms/devices/${id}`,
      revokeOthers: "/services/pms/devices/revoke-others",
      adminOverview: (vendorId: string) => `/services/pms/admin/devices?vendorId=${encodeURIComponent(vendorId)}`,
      adminDevice: (vendorId: string, id: string) => `/services/pms/admin/devices/${id}?vendorId=${encodeURIComponent(vendorId)}`,
    },
    products: {
      findAll: "/services/pms/v2/products",
      catalogFacets: "/services/pms/v2/products/catalog-facets",
      findOne: (id: string) => `/services/pms/v2/products/${id}`,
      patchOne: (id: string) => `/services/pms/v2/products/${id}`,
      deleteOne: (id: string) => `/services/pms/v2/products/${id}`,
      bulkPatch: "/services/pms/v2/products/update/bulk",
      bulkUpdate: "/services/pms/v2/products/bulk",
      bulkStatus: (batchId: string) => `/services/pms/v2/products/bulk-status/${batchId}`,
      bulkRetry: (jobId: string) => `/services/pms/v2/products/bulk-retry/${jobId}`,
      bulkAbort: (batchId: string) => `/services/pms/v2/products/bulk-abort/${batchId}`,
      bulkSession: "/services/pms/v2/products/bulk-session",
      bulkSessionStatus: (sessionId: string) => `/services/pms/v2/products/bulk-session/${sessionId}`,
      batchHistory: "/services/pms/v2/products/batch-history",
      generateSeamlessForPattern: (pattern: string) => `/services/pms/v2/products/seamless-${pattern}`,
      seamlessJobStatus: (jobId: string) => `/services/pms/v2/products/seamless-status/${jobId}`,
      seamlessJobs: "/services/pms/v2/products/seamless-jobs",
      seamlessRetry: (jobId: string) => `/services/pms/v2/products/seamless-retry/${jobId}`,
      updateCustomFieldFilters: "/services/pms/v2/products/filters/update-custom-fields",
      scrape: "/services/pms/v2/products/scrape",
      scrapeImage: "/services/pms/v2/products/scrape/image"
    },
    users: {
      findAll: "/users",
      patch: "/users",
      findOne: (id: string) => `/users/${id}`,
      deleteOne: (id: string) => `/users/${id}`,
    },
    vendors: {
      findAll: "/services/pms/vendors",
      findOne: (id: string) => `/services/pms/vendors/${id}`,
      deleteOne: (id: string) => `/services/pms/vendors/${id}`,
      create: "/services/pms/vendors",
      patch: (id: string) => `/services/pms/vendors/${id}`,
      convert: (id: string) => `/services/pms/vendors/${id}/convert`,
      toProspect: (id: string) => `/services/pms/vendors/${id}/to-prospect`,
      arPublicFiles: (id: string) => `/services/pms/config/vendors/${id}/ar-public-files`,
      findAreas: "/services/pms/vendors/areas",
      adminStats: "/services/pms/vendors/admin-stats",
    },
    spaces: {
      findAll: "/services/pms/spaces",
      findOne: (id: string) => `/services/pms/spaces/${id}`,
      deleteOne: (id: string) => `/services/pms/spaces/${id}`,
      create: "/services/pms/spaces",
      patch: "/services/pms/spaces",
      analysis: "/analysis",
      patchMasks: (id: string) => `/services/pms/spaces/${id}/masks`,
      putZones: (id: string) => `/services/pms/spaces/${id}/zones`,
      analyzeAsync: "/services/pms/spaces/analysis/async",
      analysisStatus: (jobId: string) => `/services/pms/spaces/analysis/status/${jobId}`,
      findAllSpaceTypes: "/services/pms/spaces/spaceTypes",
      assignSpaces: "/services/pms/spaces/assign",
      demoDefault: (id: string) => `/services/pms/spaces/${id}/demo-default`,
    },
    analytics: {
      // Legacy endpoints
      findAll: "/services/pms/vendors/stats",
      search: "/services/pms/events/search",
      timeline: "/services/pms/events/timeline",
      stats: "/services/pms/events/stats",
      
      // New enhanced endpoints
      dashboard: "/services/pms/analytics/dashboard",
      trends: "/services/pms/analytics/trends",
      products: "/services/pms/analytics/products",
      funnel: "/services/pms/analytics/funnel",
      realtime: "/services/pms/analytics/realtime",
      export: "/services/pms/analytics/export",
    },
    leads: {
      findAll: "/services/pms/leads",
      findOne: (id: string) => `/services/pms/leads/${id}`,
      deleteOne: (id: string) => `/services/pms/leads/${id}`,
      create: "/services/pms/leads",
      patch: (id: string) => `/services/pms/leads/${id}`,
      extract: "/services/pms/leads/refresh",
    },
    shopperInsights: {
      overview: "/services/pms/shopper-insights/overview",
      questions: "/services/pms/shopper-insights/questions",
      conversations: "/services/pms/shopper-insights/conversations",
      conversation: (uuid: string) => `/services/pms/shopper-insights/conversations/${uuid}`,
      returning: "/services/pms/shopper-insights/returning",
      consentSeen: "/services/pms/shopper-insights/consent-seen",
      // The shop's team in the chat (shopper-handoff in the API).
      handoffSummary: "/services/pms/shopper-insights/handoff/summary",
      reply: (uuid: string) => `/services/pms/shopper-insights/conversations/${uuid}/reply`,
      take: (uuid: string) => `/services/pms/shopper-insights/conversations/${uuid}/take`,
      close: (uuid: string) => `/services/pms/shopper-insights/conversations/${uuid}/close`,
    },
    storeKnowledge: {
      view: "/services/pms/store-knowledge",
      recrawl: "/services/pms/store-knowledge/recrawl",
      facts: "/services/pms/store-knowledge/facts",
      contact: "/services/pms/store-knowledge/contact",
      adminView: (vendorId: string) => `/services/pms/admin/store-knowledge/${vendorId}`,
      adminRecrawl: (vendorId: string) => `/services/pms/admin/store-knowledge/${vendorId}/recrawl`,
    },
    ads: {
      overview: "/services/pms/ads/overview",
      campaigns: "/services/pms/ads/campaigns",
      campaign: (provider: string, externalId: string) => `/services/pms/ads/campaigns/${provider}/${encodeURIComponent(externalId)}`,
      campaignStatus: (provider: string, externalId: string) => `/services/pms/ads/campaigns/${provider}/${encodeURIComponent(externalId)}/status`,
      campaignBudget: (provider: string, externalId: string) => `/services/pms/ads/campaigns/${provider}/${encodeURIComponent(externalId)}/budget`,
      budgets: "/services/pms/ads/budgets",
      connections: "/services/pms/ads/connections",
      pendingConnections: "/services/pms/ads/connections/pending",
      authorize: (provider: string) => `/services/pms/ads/connections/${provider}/authorize`,
      selectConnections: "/services/pms/ads/connections/select",
      connection: (uuid: string) => `/services/pms/ads/connections/${uuid}`,
      sync: "/services/pms/ads/sync",
      audiences: "/services/pms/ads/audiences",
      audiencePreview: "/services/pms/ads/audiences/preview",
      audiencePush: (uuid: string) => `/services/pms/ads/audiences/${uuid}/push`,
      audience: (uuid: string) => `/services/pms/ads/audiences/${uuid}`,
      suggestions: "/services/pms/ads/suggestions",
      refreshSuggestions: "/services/pms/ads/suggestions/refresh",
      applySuggestion: (uuid: string) => `/services/pms/ads/suggestions/${uuid}/apply`,
      dismissSuggestion: (uuid: string) => `/services/pms/ads/suggestions/${uuid}/dismiss`,
    },
    email: {
      send: "/email/send"
    },
    settings: {
      findConfig: "/services/pms/config",
      patch: "/services/pms/config",
      uploadLogo: "/services/pms/config/upload-logo"
    },
    productFamilies: {
      findAll: "/services/pms/product-families",
      findOne: (id: string) => `/services/pms/product-families/${id}`,
      create: "/services/pms/product-families",
      patch: (id: string) => `/services/pms/product-families/${id}`,
      deleteOne: (id: string) => `/services/pms/product-families/${id}`,
      reorder: "/services/pms/product-families/reorder",
      vendorFamilies: (vendorId: string) => `/services/pms/product-families/vendor/${vendorId}`,
    },
    productTypes: {
      findAll: "/services/pms/product-types",
      findOne: (id: string) => `/services/pms/product-types/${id}`,
      create: "/services/pms/product-types",
      patch: (id: string) => `/services/pms/product-types/${id}`,
      deleteOne: (id: string) => `/services/pms/product-types/${id}`,
    },
    model: {
      generate: "/services/pms/model/generate",
      jobStatus: (jobId: string) => `/services/pms/model/status/${jobId}`,
    },
    texture: {
      generateForPattern: (pattern: string) => `/services/pms/products/seamless-${pattern}`,
      jobStatus: (jobId: string) => `/services/pms/products/seamless-status/${jobId}`,
      jobs: "/services/pms/products/seamless-jobs",
      retry: (jobId: string) => `/services/pms/products/seamless-retry/${jobId}`,
    },
    apiIntegration: {
      get: '/services/pms/v2/api-integration',
      save: '/services/pms/v2/api-integration',
      delete: '/services/pms/v2/api-integration',
      testConnection: '/services/pms/v2/api-integration/test-connection',
      fetchSample: '/services/pms/v2/api-integration/fetch-sample',
      sync: '/services/pms/v2/api-integration/sync',
      syncStatus: (jobId: string) => `/services/pms/v2/api-integration/sync/status/${jobId}`,
      latestSync: '/services/pms/v2/api-integration/sync/latest',
      aiMap: '/services/pms/v2/api-integration/ai-map',
    },
    categories: {
      tree: "/services/pms/categories/tree",
      findAll: "/services/pms/categories",
      create: "/services/pms/categories",
      update: (id: string) => `/services/pms/categories/${id}`,
      move: (id: string) => `/services/pms/categories/${id}/move`,
      deleteOne: (id: string) => `/services/pms/categories/${id}`,
      assignProducts: (id: string) => `/services/pms/categories/${id}/products`,
      removeProducts: (id: string) => `/services/pms/categories/${id}/products`,
      reorder: "/services/pms/categories/reorder",
      removeAll: "/services/pms/categories/all",
      getProducts: (id: string) => `/services/pms/categories/${id}/products`,
      autoCategorize: "/services/pms/categories/auto-categorize",
      generateCover: (id: string) => `/services/pms/categories/${id}/cover/generate`,
      uploadCover: (id: string) => `/services/pms/categories/${id}/cover`,
      removeCover: (id: string) => `/services/pms/categories/${id}/cover`,
    },
    integration: {
      config: (alias: string) => `/services/pms/integration/${alias}/config`,
      check: (alias: string, identifier: string) => `/services/pms/integration/${alias}/check/${identifier}`,
      verify: (alias: string) => `/services/pms/integration/${alias}/verify`,
      testMatch: '/services/pms/integration/test-match',
      checkDomain: (domain: string) => `/services/pms/integration/check-domain/${encodeURIComponent(domain)}`,
      verifyScript: (domain: string) => `/services/pms/integration/verify-script/${encodeURIComponent(domain)}`,
      pageSnapshot: '/services/pms/integration/page-snapshot',
      placementSuggestion: '/services/pms/integration/placement-suggestion',
    },
    contentStudio: {
      // Projects
      listProjects: "/services/pms/content-studio/projects",
      getProject: (uuid: string) => `/services/pms/content-studio/projects/${uuid}`,
      createProject: "/services/pms/content-studio/projects",
      updateProject: (uuid: string) => `/services/pms/content-studio/projects/${uuid}`,
      deleteProject: (uuid: string) => `/services/pms/content-studio/projects/${uuid}`,
      // Assets
      listAssets: "/services/pms/content-studio/assets",
      getAsset: (uuid: string) => `/services/pms/content-studio/assets/${uuid}`,
      updateAssetPosition: (uuid: string) => `/services/pms/content-studio/assets/${uuid}/position`,
      updateAssetNote: (uuid: string) => `/services/pms/content-studio/assets/${uuid}/note`,
      deleteAsset: (uuid: string) => `/services/pms/content-studio/assets/${uuid}`,
      // Generations
      createGeneration: "/services/pms/content-studio/generations",
      listGenerations: "/services/pms/content-studio/generations",
      getGeneration: (jobId: string) => `/services/pms/content-studio/generations/${jobId}`,
      cancelGeneration: (jobId: string) => `/services/pms/content-studio/generations/${jobId}/cancel`,
      // Recipes
      listRecipes: "/services/pms/content-studio/recipes",
      // The shared 3D-studio prop library — global, the same shelf for every vendor.
      listStudioProps: "/services/pms/content-studio/props",
      listStudioPropCategories: "/services/pms/content-studio/props/categories",
      studioPropModel: (propId: string) => `/services/pms/content-studio/props/${propId}/model`,
      createStudioProp: "/services/pms/content-studio/props",
      cutoutImage: "/services/pms/content-studio/cutout",
      studioPropStatus: (propId: string) => `/services/pms/content-studio/props/${propId}/status`,
      deleteStudioProp: (propId: string) => `/services/pms/content-studio/props/${propId}`,
      // Brand Kit
      getBrandKit: "/services/pms/content-studio/brand-kit",
      updateBrandKit: "/services/pms/content-studio/brand-kit",
      extractBrandIdentity: "/services/pms/content-studio/brand-kit/extract",
      discoverBrand: "/services/pms/content-studio/brand-kit/discover",
      lookAgain: "/services/pms/content-studio/brand-kit/look-again",
      // Style reference upload
      uploadStyleRef: "/services/pms/content-studio/upload-style-ref",
      // Inspiration: the vendor pastes a picture's address and we keep our own
      // copy, because remote addresses expire and hotlink-block.
      inspirationFromUrl: "/services/pms/content-studio/inspiration-from-url",
      // From Reference — detect replaceable objects in the style image (YOLO-World)
      detectReferenceObjects: "/services/pms/content-studio/detect-reference-objects",
      measureReferenceCamera: "/services/pms/content-studio/measure-reference-camera",
      analyzeRoom: "/services/pms/content-studio/rooms/analyze",
      importImage: (projectUuid: string) => `/services/pms/content-studio/projects/${projectUuid}/assets/import`,
      // Music bed for the timeline editor (generated server-side).
      generateMusic: "/services/pms/content-studio/music",
      // Video Story: brief + products (+ inspiration frames) → storyboard.
      storyboard: "/services/pms/content-studio/storyboard",
      // Studio board: reference breakdown and the price list.
      studioAnalyzeReference: "/services/pms/content-studio/studio/analyze-reference",
      studioPricing: "/services/pms/content-studio/studio/pricing",
      roomAnalysisStatus: (jobId: string) => `/services/pms/content-studio/rooms/analyze/${jobId}`,
      roomScene: (spaceId: string) => `/services/pms/content-studio/rooms/${spaceId}/scene`,
      // Agent runs — durable, so the stream is a subscription to a run rather
      // than the run itself; the work continues if the client disconnects.
      createAgentRun: "/services/pms/content-studio/agent-runs",
      listAgentRuns: "/services/pms/content-studio/agent-runs",
      getAgentRun: (runId: string) => `/services/pms/content-studio/agent-runs/${runId}`,
      streamAgentRun: (runId: string) => `/services/pms/content-studio/agent-runs/${runId}/stream`,
      cancelAgentRun: (runId: string) => `/services/pms/content-studio/agent-runs/${runId}/cancel`,
      rateAgentRun: (runId: string) => `/services/pms/content-studio/agent-runs/${runId}/feedback`,
      markAgentRunApplied: (runId: string) => `/services/pms/content-studio/agent-runs/${runId}/applied`,
      approveAgentStep: (runId: string, index: number) => `/services/pms/content-studio/agent-runs/${runId}/steps/${index}/approve`,
      resumeAgentRun: (runId: string) => `/services/pms/content-studio/agent-runs/${runId}/resume`,
      declineAgentStep: (runId: string, index: number) => `/services/pms/content-studio/agent-runs/${runId}/steps/${index}/decline`,
      listAgentThreads: "/services/pms/content-studio/agent-threads",
      getAgentThread: (threadId: string) => `/services/pms/content-studio/agent-threads/${threadId}`,
      closeAgentThread: (threadId: string) => `/services/pms/content-studio/agent-threads/${threadId}/close`,
      // The agent's own callback loop. Armed here, watched and fired by the
      // server, so a follow-up survives the tab being closed.
      agentContinuations: '/services/pms/content-studio/agent-continuations',
      cancelAgentContinuation: (continuationId: string) =>
        `/services/pms/content-studio/agent-continuations/${continuationId}`,
      // Credits
      creditsBalance: "/services/pms/content-studio/credits/balance",
      creditsHistory: "/services/pms/content-studio/credits/history",
      // Super-admin credits inspection + adjustment (per vendor)
      adminCreditsForVendor: (vendorId: string) =>
        `/services/pms/content-studio/admin/credits/${vendorId}`,
      adminSetCreditsForVendor: (vendorId: string) =>
        `/services/pms/content-studio/admin/credits/${vendorId}/set`,
    },
    socialScheduler: {
      // Accounts
      listAccounts: "/services/pms/social-scheduler/accounts",
      getAccount: (uuid: string) => `/services/pms/social-scheduler/accounts/${uuid}`,
      connectAccount: "/services/pms/social-scheduler/accounts",
      updateAccount: (uuid: string) => `/services/pms/social-scheduler/accounts/${uuid}`,
      disconnectAccount: (uuid: string) => `/services/pms/social-scheduler/accounts/${uuid}`,
      affectedPosts: (uuid: string) => `/services/pms/social-scheduler/accounts/${uuid}/affected-posts`,
      pauseAccount: (uuid: string) => `/services/pms/social-scheduler/accounts/${uuid}/pause`,
      resumeAccount: (uuid: string) => `/services/pms/social-scheduler/accounts/${uuid}/resume`,
      metaOAuthStatus: "/services/pms/social-scheduler/oauth/meta/status",
      metaOAuthStart: "/services/pms/social-scheduler/oauth/meta/start",
      // Posts
      listPosts: "/services/pms/social-scheduler/posts",
      getPost: (uuid: string) => `/services/pms/social-scheduler/posts/${uuid}`,
      createPost: "/services/pms/social-scheduler/posts",
      updatePost: (uuid: string) => `/services/pms/social-scheduler/posts/${uuid}`,
      deletePost: (uuid: string) => `/services/pms/social-scheduler/posts/${uuid}`,
      schedulePost: (uuid: string) => `/services/pms/social-scheduler/posts/${uuid}/schedule`,
      unschedulePost: (uuid: string) => `/services/pms/social-scheduler/posts/${uuid}/unschedule`,
      calendarPosts: "/services/pms/social-scheduler/posts/calendar",
      generateCaption: "/services/pms/social-scheduler/posts/generate-caption",
      // Metrics
      getPostMetrics: (uuid: string) => `/services/pms/social-scheduler/posts/${uuid}/metrics`,
      getPostMetricsHistory: (uuid: string) => `/services/pms/social-scheduler/posts/${uuid}/metrics/history`,
      // Dashboard
      dashboard: "/services/pms/social-scheduler/dashboard",
    },
    // Pinterest as a Studio inspiration source (src/modules/vendor/pinterest).
    pinterest: {
      status: "/services/pms/pinterest/status",
      oauthStart: "/services/pms/pinterest/oauth/start",
      disconnect: "/services/pms/pinterest/account",
      boards: "/services/pms/pinterest/boards",
      boardPins: (boardId: string) => `/services/pms/pinterest/boards/${boardId}/pins`,
      myPins: "/services/pms/pinterest/pins",
      resolve: "/services/pms/pinterest/resolve",
      import: "/services/pms/pinterest/import",
    },
    plans: {
      list: '/services/pms/plans',
      get: (id: string) => `/services/pms/plans/${id}`,
      create: '/services/pms/plans',
      update: (id: string) => `/services/pms/plans/${id}`,
      delete: (id: string) => `/services/pms/plans/${id}`,
      reorder: '/services/pms/plans/reorder',
      myPlan: '/services/pms/plans/my-plan',
      changeMyPlan: '/services/pms/plans/my-plan',
      grantMonthlyCreditsAll: '/services/pms/plans/admin/grant-monthly-credits',
      // Pricing v2 (docs/pricing-v2.md, API repo)
      rateCard: '/services/pms/plans/rate-card',
      vendorTrial: (vendorId: string) => `/services/pms/plans/vendors/${vendorId}/trial`,
      vendorContract: (vendorId: string) => `/services/pms/plans/vendors/${vendorId}/contract`,
      vendorOffer: (vendorId: string) => `/services/pms/plans/vendors/${vendorId}/offer`,
    },
    billing: {
      checkoutSession: '/services/pms/billing/checkout-session',
      embeddedCheckout: '/services/pms/billing/embedded-checkout',
      portalSession: '/services/pms/billing/portal-session',
      config: '/services/pms/billing/config',
      // Wire-transfer (manual invoice) — vendor
      wireInvoice: '/services/pms/billing/wire-invoice',
      myInvoices: '/services/pms/billing/my-invoices',
      myStripeInvoices: '/services/pms/billing/my-stripe-invoices',
      wireConfig: '/services/pms/billing/wire-config',
      invoicePdf: (id: string) => `/services/pms/billing/invoices/${id}/pdf`,
      // Wire-transfer — super-admin
      adminPlanStripePrice: (id: string) => `/services/pms/billing/admin/plans/${id}/stripe-price`,
      adminVendorInvoices: (id: string) => `/services/pms/billing/admin/vendors/${id}/invoices`,
      adminHistoricalInvoices: (id: string) => `/services/pms/billing/admin/vendors/${id}/historical-invoices`,
      adminInvoice: (id: string) => `/services/pms/billing/admin/invoices/${id}`,
      adminImportWire: (id: string) => `/services/pms/billing/admin/vendors/${id}/import-wire`,
      adminVendorWirePrice: (id: string) => `/services/pms/billing/admin/vendors/${id}/wire-price`,
      adminVendorStripeReprice: (id: string) => `/services/pms/billing/admin/vendors/${id}/stripe-reprice`,
      adminInvoices: '/services/pms/billing/admin/invoices',
      adminCreateInvoice: '/services/pms/billing/admin/invoices',
      adminMarkPaid: (id: string) => `/services/pms/billing/admin/invoices/${id}/mark-paid`,
      adminVoidInvoice: (id: string) => `/services/pms/billing/admin/invoices/${id}/void`,
      adminInvoicePdf: (id: string) => `/services/pms/billing/admin/invoices/${id}/pdf`,
      adminWireSettings: '/services/pms/billing/admin/wire-settings',
      adminRunWireCron: '/services/pms/billing/admin/wire/run-cron',
      // Enterprise quote requests
      quoteRequest: '/services/pms/billing/quote-request',
      myQuoteRequests: '/services/pms/billing/my-quote-requests',
      quoteCheckout: '/services/pms/billing/quote-checkout',
      quoteWireInvoice: '/services/pms/billing/quote-wire-invoice',
      adminQuoteRequests: '/services/pms/billing/admin/quote-requests',
      adminQuoteUpdate: (id: string) => `/services/pms/billing/admin/quote-requests/${id}`,
      adminQuoteSetupBilling: (id: string) => `/services/pms/billing/admin/quote-requests/${id}/setup-billing`,
    },
    // Internal sales CRM (super-admin, allowlisted by email on the API).
    crm: {
      access: '/services/pms/crm/access',
      import: '/services/pms/crm/import',
      contacts: '/services/pms/crm/contacts',
      list: (kind: string) => `/services/pms/crm/${kind}`,
      record: (kind: string, id: string) => `/services/pms/crm/${kind}/${id}`,
      reorder: (kind: string) => `/services/pms/crm/${kind}/reorder`,
      toProspect: (leadId: string) => `/services/pms/crm/leads/${leadId}/prospect`,
      leadVendor: (leadId: string) => `/services/pms/crm/leads/${leadId}/vendor`,
      vendors: '/services/pms/crm/vendors',
    },
    demoSites: {
      list: '/services/pms/demo-sites',
      publish: '/services/pms/demo-sites',
      slugStatus: '/services/pms/demo-sites/slug-status',
      unpublish: (slug: string) => `/services/pms/demo-sites/${encodeURIComponent(slug)}`,
      quality: (slug: string) => `/services/pms/demo-sites/${encodeURIComponent(slug)}/quality`,
      expiry: (slug: string) => `/services/pms/demo-sites/${encodeURIComponent(slug)}/expiry`,
      visitSummary: '/services/pms/demo-sites/visits',
      visits: (slug: string) => `/services/pms/demo-sites/${encodeURIComponent(slug)}/visits`,
      tracking: (slug: string) => `/services/pms/demo-sites/${encodeURIComponent(slug)}/tracking`,
      trackingEverywhere: '/services/pms/demo-sites/tracking',
      generatorScan: '/services/pms/demo-sites/generator/scan',
      generate: '/services/pms/demo-sites/generator',
      rugModel: '/services/pms/demo-sites/generator/rug-model',
      furnitureModel: '/services/pms/demo-sites/generator/furniture-model',
      furnitureModelStatus: (jobId: string) => `/services/pms/demo-sites/generator/furniture-model/${encodeURIComponent(jobId)}`,
      furnitureModelGlb: (jobId: string) => `/services/pms/demo-sites/generator/furniture-model/${encodeURIComponent(jobId)}/glb`,
      generatorTiles: '/services/pms/demo-sites/generator/tiles',
      generatorProductStatus: (batchId: string) => `/services/pms/demo-sites/generator/product-status/${encodeURIComponent(batchId)}`,
    },
    notifications: {
      findAll: '/services/pms/notifications',
      unreadCount: '/services/pms/notifications/unread-count',
      markRead: (id: string) => `/services/pms/notifications/${id}/read`,
      markAllRead: '/services/pms/notifications/read-all',
      summary: '/services/pms/notifications/summary',
      preferences: '/services/pms/notifications/preferences',
      push: {
        config: '/services/pms/notifications/push/config',
        subscriptions: '/services/pms/notifications/push/subscriptions',
      },
    },
  };